/**
 * Channel Partner API Service
 * Handles all channel partner-related API calls (onboarding, agreements, commissions, payments)
 */

import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';

// Create axios instance with base configuration
const partnerAPI = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 60000
});

// Add token to all requests
partnerAPI.interceptors.request.use(config => {
  // Try to get token from various storage locations in order of preference
  let token = null;

  // 1. Try direct token keys
  token =
    localStorage.getItem('token') ||
    sessionStorage.getItem('token') ||
    localStorage.getItem('authToken') ||
    sessionStorage.getItem('authToken') ||
    localStorage.getItem('jwt_token') ||
    sessionStorage.getItem('jwt_token');

  // 2. If not found, try to extract from authInfo object
  if (!token) {
    try {
      const authInfoStr = localStorage.getItem('authInfo');
      if (authInfoStr) {
        const authInfo = JSON.parse(authInfoStr);
        if (authInfo.token) {
          token = authInfo.token;
          console.log('[ChannelPartnerAPI] Token extracted from authInfo');
        }
      }
    } catch (e) {
      console.warn('[ChannelPartnerAPI] Could not parse authInfo:', e);
    }
  }

  // 3. Also check sessionStorage authInfo
  if (!token) {
    try {
      const authInfoStr = sessionStorage.getItem('authInfo');
      if (authInfoStr) {
        const authInfo = JSON.parse(authInfoStr);
        if (authInfo.token) {
          token = authInfo.token;
          console.log('[ChannelPartnerAPI] Token extracted from sessionStorage authInfo');
        }
      }
    } catch (e) {
      console.warn('[ChannelPartnerAPI] Could not parse sessionStorage authInfo:', e);
    }
  }

  if (token) {
    // Ensure token is properly formatted
    const bearerToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
    config.headers.Authorization = bearerToken;
    console.log('[ChannelPartnerAPI] Token added to request headers ✓');
  } else {
    console.warn('[ChannelPartnerAPI] No token found in storage. Will get 401 Unauthorized.');
  }

  return config;
});

// Handle 401 responses
partnerAPI.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      console.warn('Unauthorized (401): Token may be expired or invalid. Check localStorage/sessionStorage for valid token.');
      // Optionally redirect to login
      // window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/**
 * Channel Partner Onboarding
 */
export const ChannelPartnerOnboardingAPI = {
  /**
   * Create channel partner (direct creation)
   * @param {Object} partnerData - Channel partner data
   * @returns {Promise<Object>} Partner creation confirmation
   */
  createChannelPartner: async (partnerData) => {
    try {
      const response = await partnerAPI.post('/api/channel-partners', partnerData);
      return response.data;
    } catch (error) {
      console.error('Error creating channel partner:', error);
      throw error;
    }
  },

  /**
   * Submit channel partner onboarding application
   * @param {Object} applicationData - Onboarding application data
   * @returns {Promise<Object>} Application confirmation
   */
  submitApplication: async (applicationData) => {
    try {
      const response = await partnerAPI.post('/api/channel-partners/onboarding/apply', applicationData);
      return response.data;
    } catch (error) {
      console.error('Error submitting channel partner application:', error);
      throw error;
    }
  },

  /**
   * Get onboarding application by ID
   * @param {string} applicationId - Application ID
   * @returns {Promise<Object>} Application details
   */
  getApplication: async (applicationId) => {
    try {
      const response = await partnerAPI.get(`/api/channel-partners/onboarding/${applicationId}`);
      return response.data?.data || {};
    } catch (error) {
      console.error('Error fetching onboarding application:', error);
      throw error;
    }
  },

  /**
   * Get all onboarding applications (all statuses)
   * @param {Object} params - Query parameters
   * @returns {Promise<Array>} List of all applications with all statuses
   */
  getApplications: async (params = {}) => {
    try {
      const response = await partnerAPI.get('/api/channel-partners/onboarding/all', { params });
      return response.data || [];
    } catch (error) {
      console.error('Error fetching onboarding applications:', error);
      throw error;
    }
  },

  /**
   * Approve onboarding application
   * @param {string} applicationId - Application ID
   * @param {Object} approvalData - Approval details
   * @returns {Promise<Object>} Approval confirmation
   */
  approveApplication: async (applicationId, approvalData = {}) => {
    try {
      const response = await partnerAPI.post(
        `/api/channel-partners/onboarding/${applicationId}/approve`,
        approvalData
      );
      return response.data;
    } catch (error) {
      console.error('Error approving onboarding application:', error);
      throw error;
    }
  },

  /**
   * Reject onboarding application
   * @param {string} applicationId - Application ID
   * @param {string} reason - Rejection reason
   * @returns {Promise<Object>} Rejection confirmation
   */
  rejectApplication: async (applicationId, reason = '') => {
    try {
      const response = await partnerAPI.post(
        `/api/channel-partners/onboarding/${applicationId}/reject`,
        { reason }
      );
      return response.data;
    } catch (error) {
      console.error('Error rejecting onboarding application:', error);
      throw error;
    }
  }
};

/**
 * Channel Partner Agreements
 */
export const ChannelPartnerAgreementAPI = {
  /**
   * Create a new partnership agreement
   * @param {Object} agreementData - Agreement details
   * @returns {Promise<Object>} Agreement confirmation
   */
  createAgreement: async (agreementData) => {
    try {
      const response = await partnerAPI.post('/api/channel-partners/agreements/create', agreementData);
      return response.data;
    } catch (error) {
      console.error('Error creating partnership agreement:', error);
      throw error;
    }
  },

  /**
   * Get agreement by ID
   * @param {string} agreementId - Agreement ID
   * @returns {Promise<Object>} Agreement details
   */
  getAgreement: async (agreementId) => {
    try {
      const response = await partnerAPI.get(`/api/channel-partners/agreements/${agreementId}`);
      return response.data?.data || {};
    } catch (error) {
      console.error('Error fetching agreement:', error);
      throw error;
    }
  },

  /**
   * Get all agreements for a partner
   * @param {string} partnerId - Partner ID
   * @returns {Promise<Array>} List of agreements
   */
  getAgreementsByPartner: async (partnerId) => {
    try {
      const response = await partnerAPI.get('/api/channel-partners/agreements', {
        params: { partnerId }
      });
      return response.data?.data || [];
    } catch (error) {
      console.error('Error fetching agreements for partner:', error);
      throw error;
    }
  },

  /**
   * Activate a partnership agreement
   * @param {string} agreementId - Agreement ID
   * @returns {Promise<Object>} Activation confirmation
   */
  activateAgreement: async (agreementId) => {
    try {
      const response = await partnerAPI.post(
        `/api/channel-partners/agreements/${agreementId}/activate`
      );
      return response.data;
    } catch (error) {
      console.error('Error activating agreement:', error);
      throw error;
    }
  },

  /**
   * Terminate a partnership agreement
   * @param {string} agreementId - Agreement ID
   * @param {string} reason - Termination reason
   * @returns {Promise<Object>} Termination confirmation
   */
  terminateAgreement: async (agreementId, reason = '') => {
    try {
      const response = await partnerAPI.post(
        `/api/channel-partners/agreements/${agreementId}/terminate`,
        { reason }
      );
      return response.data;
    } catch (error) {
      console.error('Error terminating agreement:', error);
      throw error;
    }
  }
};

/**
 * Channel Partner Commissions
 */
export const ChannelPartnerCommissionAPI = {
  /**
   * Get commission earnings for a partner
   * @param {string} partnerId - Partner ID
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Commission details
   */
  getCommissions: async (partnerId, params = {}) => {
    try {
      const response = await partnerAPI.get(`/api/channel-partners/${partnerId}/commissions`, { params });
      return response.data?.data || {};
    } catch (error) {
      console.error('Error fetching commissions:', error);
      throw error;
    }
  },

  /**
   * Get commission breakdown by ticket
   * @param {string} partnerId - Partner ID
   * @returns {Promise<Array>} Commission details by ticket
   */
  getCommissionBreakdown: async (partnerId) => {
    try {
      const response = await partnerAPI.get(`/api/channel-partners/${partnerId}/commissions/breakdown`);
      return response.data?.data || [];
    } catch (error) {
      console.error('Error fetching commission breakdown:', error);
      throw error;
    }
  },

  /**
   * Get pending commissions (awaiting approval/payout)
   * @returns {Promise<Array>} List of pending commissions
   */
  getPendingCommissions: async () => {
    try {
      const response = await partnerAPI.get('/api/finance/pending-summary');
      return response.data?.data?.channelCommissions?.items || [];
    } catch (error) {
      console.error('Error fetching pending commissions:', error);
      throw error;
    }
  }
};

/**
 * Channel Partner Payments
 */
export const ChannelPartnerPaymentAPI = {
  /**
   * Get payment history for a partner
   * @param {string} partnerId - Partner ID
   * @param {Object} params - Query parameters
   * @returns {Promise<Array>} List of payments
   */
  getPaymentHistory: async (partnerId, params = {}) => {
    try {
      const response = await partnerAPI.get(`/api/channel-partners/${partnerId}/payments`, { params });
      return response.data?.data || [];
    } catch (error) {
      console.error('Error fetching payment history:', error);
      throw error;
    }
  },

  /**
   * Get pending payment requests for a partner
   * @param {string} partnerId - Partner ID
   * @returns {Promise<Array>} List of pending payments
   */
  getPendingPayments: async (partnerId) => {
    try {
      const response = await partnerAPI.get(`/api/channel-partners/${partnerId}/payments/pending`);
      return response.data?.data || [];
    } catch (error) {
      console.error('Error fetching pending payments:', error);
      throw error;
    }
  },

  /**
   * Approve a pending payment
   * @param {string} paymentId - Payment ID
   * @returns {Promise<Object>} Approval confirmation
   */
  approvePayment: async (paymentId) => {
    try {
      const response = await partnerAPI.post(`/api/channel-partners/payments/${paymentId}/approve`);
      return response.data;
    } catch (error) {
      console.error('Error approving payment:', error);
      throw error;
    }
  },

  /**
   * Process payment for a partner
   * @param {string} paymentId - Payment ID
   * @param {Object} paymentDetails - Bank transfer details
   * @returns {Promise<Object>} Payment confirmation
   */
  processPayment: async (paymentId, paymentDetails) => {
    try {
      const response = await partnerAPI.post(
        `/api/channel-partners/payments/${paymentId}/process`,
        paymentDetails
      );
      return response.data;
    } catch (error) {
      console.error('Error processing payment:', error);
      throw error;
    }
  },

  /**
   * Confirm payment received
   * @param {string} paymentId - Payment ID
   * @param {Object} confirmationData - Confirmation details
   * @returns {Promise<Object>} Confirmation response
   */
  confirmPaymentReceived: async (paymentId, confirmationData = {}) => {
    try {
      const response = await partnerAPI.post(
        `/api/channel-partners/payments/${paymentId}/confirm`,
        confirmationData
      );
      return response.data;
    } catch (error) {
      console.error('Error confirming payment:', error);
      throw error;
    }
  }
};

/**
 * Service Partner Payments
 */
export const ServicePartnerPaymentAPI = {
  /**
   * Get pending service partner payments
   * @returns {Promise<Array>} List of pending payments
   */
  getPendingPayments: async () => {
    try {
      const response = await partnerAPI.get('/api/finance/pending-summary');
      return response.data?.data?.partnerPayments?.items || [];
    } catch (error) {
      console.error('Error fetching pending service partner payments:', error);
      throw error;
    }
  },

  /**
   * Get approved payments awaiting transfer
   * @returns {Promise<Array>} List of approved payments
   */
  getApprovedPayments: async () => {
    try {
      const response = await partnerAPI.get('/api/finance/pending-summary');
      return response.data?.data?.approvedAwaitingTransfer?.items || [];
    } catch (error) {
      console.error('Error fetching approved service partner payments:', error);
      throw error;
    }
  },

  /**
   * Get in-transit payments
   * @returns {Promise<Array>} List of in-transit payments
   */
  getInTransitPayments: async () => {
    try {
      const response = await partnerAPI.get('/api/finance/pending-summary');
      return response.data?.data?.inTransit?.items || [];
    } catch (error) {
      console.error('Error fetching in-transit payments:', error);
      throw error;
    }
  },

  /**
   * Approve service partner payment
   * @param {string} paymentId - Payment ID
   * @returns {Promise<Object>} Approval confirmation
   */
  approvePayment: async (paymentId) => {
    try {
      const response = await partnerAPI.post(`/api/service-partners/payments/${paymentId}/approve`);
      return response.data;
    } catch (error) {
      console.error('Error approving service partner payment:', error);
      throw error;
    }
  },

  /**
   * Process bank transfer for service partner payment
   * @param {string} paymentId - Payment ID
   * @param {Object} transferDetails - Bank transfer details
   * @returns {Promise<Object>} Transfer confirmation
   */
  processTransfer: async (paymentId, transferDetails) => {
    try {
      const response = await partnerAPI.post(
        `/api/service-partners/payments/${paymentId}/transfer`,
        transferDetails
      );
      return response.data;
    } catch (error) {
      console.error('Error processing service partner transfer:', error);
      throw error;
    }
  },

  /**
   * Confirm payment received by service partner
   * @param {string} paymentId - Payment ID
   * @param {Object} confirmationData - Confirmation details
   * @returns {Promise<Object>} Confirmation response
   */
  confirmPaymentReceived: async (paymentId, confirmationData = {}) => {
    try {
      const response = await partnerAPI.post(
        `/api/service-partners/payments/${paymentId}/confirm`,
        confirmationData
      );
      return response.data;
    } catch (error) {
      console.error('Error confirming service partner payment:', error);
      throw error;
    }
  }
};

export default partnerAPI;
